'use client';

import React, { useRef } from 'react';
import { FileIcon, FileTextIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Combobox } from '@tranquility-ai/theme-components/combobox';
import { Input } from '@tranquility-ai/theme-components/input';
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { TIMELINE_TITLE_MAX_LENGTH } from '@/shared/constants';
import type { ScopesObject } from '@/shared/types';
import { hasScopedItems, listScopedItems } from '@/shared/timeline-scope-items';
import { TimelineChoiceCards } from './timeline-choice-cards';
import './timeline-choice-cards.css';
import { TimelineScopeSummary } from './timeline-scope-summary';

export type AnswerTimelineTargetChoice = 'new' | 'existing';

export type AnswerTimelineTargetModalProps = {
  isOpen: boolean;
  submitting: boolean;
  choice: AnswerTimelineTargetChoice;
  onChoiceChange: (choice: AnswerTimelineTargetChoice) => void;
  newTitle: string;
  onNewTitleChange: (title: string) => void;
  existingOptions: { value: string; label: string }[];
  selectedTimelineId?: string;
  onSelectedTimelineChange: (id: string | undefined) => void;
  scopes: ScopesObject;
  onScopesChange: (next: ScopesObject) => void;
  canSubmit: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export function AnswerTimelineTargetModal({
  isOpen,
  submitting,
  choice,
  onChoiceChange,
  newTitle,
  onNewTitleChange,
  existingOptions,
  selectedTimelineId,
  onSelectedTimelineChange,
  scopes,
  onScopesChange,
  canSubmit,
  onCancel,
  onConfirm,
}: AnswerTimelineTargetModalProps) {
  const t = useTimePilotT();
  const trimmedTitle = newTitle.trim();
  const titleLength = trimmedTitle.length;
  const titleTooLong = titleLength > TIMELINE_TITLE_MAX_LENGTH;
  const sourcesRemain = hasScopedItems(scopes);
  const noExisting = existingOptions.length === 0;
  const initialScopesRef = useRef<ScopesObject | null>(null);
  if (isOpen && !initialScopesRef.current) {
    initialScopesRef.current = structuredClone(scopes);
  }
  if (!isOpen) {
    initialScopesRef.current = null;
  }

  const initialScopes = initialScopesRef.current;
  const scopesUnchanged =
    !!initialScopes &&
    listScopedItems(scopes)
      .map((item) => item.id)
      .join('|') ===
      listScopedItems(initialScopes)
        .map((item) => item.id)
        .join('|');

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !submitting) onCancel();
      }}
      title={t('timelineCreate.generateEvents')}
      size="md"
      bodyClassName="timeline-modal-body-pad"
      dismissOnBackdropClick={!submitting}
      dismissOnEscape={!submitting}
      showCloseButton={!submitting}
      footer={
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <Button
            type="button"
            size="sm"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('timeline.dialog.cancel')}
            disabled={submitting}
            onClick={onCancel}
          >
            {t('timeline.dialog.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            style="solid"
            color="accent"
            background="inverse"
            aria-label={t('timelineCreate.generateEvents')}
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            {t('timelineCreate.generateEvents')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <TimelineChoiceCards
          ariaLabel={t('timelineCreate.destination')}
          value={choice}
          onChange={(value) => onChoiceChange(value as AnswerTimelineTargetChoice)}
          options={[
            {
              value: 'new',
              label: t('timelineCreate.newTimeline'),
              description: t('timelineCreate.newTimelineDescription'),
              icon: <FileIcon weight="duotone" />,
              disabled: submitting,
            },
            {
              value: 'existing',
              label: t('timelineCreate.existingTimeline'),
              description: t('timelineCreate.existingTimelineDescription'),
              icon: <FileTextIcon weight="duotone" />,
              disabled: submitting || noExisting,
            },
          ]}
        />
        {choice === 'new' ? (
          <Input
            label={t('timelineCreate.nameLabel')}
            placeholder={t('timelineCreate.namePlaceholder')}
            value={newTitle}
            background="inverse"
            className="inverse-form-surface w-full"
            disabled={submitting}
            invalid={titleTooLong}
            error={
              titleTooLong
                ? t('timelineCreate.nameTooLong', { max: TIMELINE_TITLE_MAX_LENGTH })
                : undefined
            }
            description={`${titleLength}/${TIMELINE_TITLE_MAX_LENGTH}`}
            onChange={(event) => onNewTitleChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canSubmit) onConfirm();
            }}
          />
        ) : (
          <Combobox
            aria-label={t('timelineCreate.selectDestination')}
            label={t('workProducts.timeline')}
            placeholder={noExisting ? t('timeline.select.none') : t('timelineCreate.selectTimeline')}
            emptyMessage={t('timeline.select.none')}
            options={existingOptions}
            value={selectedTimelineId}
            disabled={submitting || noExisting}
            onValueChange={onSelectedTimelineChange}
          />
        )}
        <TimelineScopeSummary
          scopes={scopes}
          onChange={onScopesChange}
          title={t('timelineCreate.sourcesFromAnswer')}
          emptyText={t('timelineCreate.sourcesRequired')}
          inverse
          showClearAll={false}
          onReset={
            initialScopes
              ? () => onScopesChange(structuredClone(initialScopes))
              : undefined
          }
          resetDisabled={scopesUnchanged || submitting}
        />
        {!sourcesRemain ? (
          <p className="m-0 text-xs text-white/60">
            {t('timelineCreate.removeSourcesHint')}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
